// view/infoPopover.js — info buttons that open a short help popover by click / tap (plan documents_admin-search,
// phase tooltip). Generic: a button with [data-info-popover] and aria-controls="<id>" opens the element with that id,
// which the server renders next to it (hidden). Mechanics as the document info button (docInfoBtn in documents.js):
// one open at a time, a click elsewhere or Escape closes; a click inside the popover keeps it open.
//
// Delegated on the root, so buttons that come and go need no setup — the admin filter table (#filterTable) is replaced
// on every ajax reload. Mounted once by the admin bundle.
//
// Returns { close, destroy }.

import { createDisposers } from '../core/disposers.js';
import { onEscape } from '../userInput/escapeKey.js';

export const INFO_POPOVER_SELECTOR = '[data-info-popover]';

// keep the popover inside the window: open it to the left of its button when it would run out on the right
const EDGE_GAP = 8;

export function createInfoPopovers({ root = document } = {}) {
    const disposers = createDisposers();
    const doc = root.ownerDocument || root;
    let open = null;   // { button, popover }

    const close = () => {
        if (!open) { return; }
        open.popover.hidden = true;
        open.popover.classList.remove('topo2-info-popover--end');
        open.button.setAttribute('aria-expanded', 'false');
        open = null;
    };

    const show = (button) => {
        const id = button.getAttribute('aria-controls');
        const popover = id ? doc.getElementById(id) : null;
        if (!popover) {
            // markup error: the button points nowhere — visible in the console instead of a silent dead button
            console.error('[infoPopover] no popover for button', button);
            return;
        }
        popover.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        open = { button, popover };
        const view = doc.defaultView;
        if (view && popover.getBoundingClientRect().right > view.innerWidth - EDGE_GAP) {
            popover.classList.add('topo2-info-popover--end');
        }
    };

    const onClick = (ev) => {
        if (open && !open.button.isConnected) { open = null; }   // its table was replaced
        const button = ev.target.closest(INFO_POPOVER_SELECTOR);
        if (button && root.contains(button)) {
            const wasOpen = open && open.button === button;
            close();
            if (!wasOpen) { show(button); }
            return;
        }
        if (open && !open.popover.contains(ev.target)) { close(); }
    };

    root.addEventListener('click', onClick);
    disposers.add(() => root.removeEventListener('click', onClick));
    disposers.add(onEscape(() => {
        if (!open) { return; }
        const { button } = open;
        close();
        button.focus();
    }, { target: root }));

    return {
        close,
        destroy() {
            close();
            disposers.dispose();
        },
    };
}
